import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Users, Music, UserCheck, Eye, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import SelectedForStageTable from "@/components/selected-for-stage-table";
import ConsiderationTable from "@/components/consideration-table";
import { PageBackground } from "@/components/ui/page-background";
import type { Performer, JamNight } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FocusedViewProps {
  jamNightId: number;
  viewType?: string;
}

type ViewType = "participants" | "on-stage" | "selected" | "consideration" | "stage-combined";

const VIEW_OPTIONS: Array<{
  id: ViewType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  {
    id: "stage-combined",
    label: "Stage Overview",
    icon: Layers,
    description: "View on stage and selected together"
  },
  {
    id: "participants",
    label: "Participants",
    icon: Users,
    description: "View all signed-up participants"
  },
  {
    id: "on-stage",
    label: "On Stage",
    icon: Music,
    description: "View performers currently on stage"
  },
  {
    id: "selected",
    label: "Selected for Stage",
    icon: UserCheck,
    description: "View performers selected to go on next"
  },
  {
    id: "consideration",
    label: "Under Consideration",
    icon: Eye,
    description: "View performers being considered"
  }
];

export default function FocusedView({ jamNightId, viewType }: FocusedViewProps) {
  const [, setLocation] = useLocation();
  const [selectedView, setSelectedView] = useState<ViewType | null>(null);
  const [showSelector, setShowSelector] = useState(false);

  // Load jam night data
  const { data: jamNight, isLoading: jamNightLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
  });

  // Load performers data
  const { data: performers = [], isLoading: performersLoading } = useQuery<Performer[]>({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  const isLoading = jamNightLoading || performersLoading;
  const isEventCompleted = jamNight?.completed || false;

  // Set view from URL parameter
  useEffect(() => {
    if (viewType && VIEW_OPTIONS.some(opt => opt.id === viewType)) {
      setSelectedView(viewType as ViewType);
      setShowSelector(false);
    } else {
      // If no valid view type, show selector
      setShowSelector(true);
    }
  }, [viewType]);

  const handleViewSelect = (viewType: ViewType) => {
    setSelectedView(viewType);
    setShowSelector(false);
  };

  const handleClose = () => {
    setLocation(`/event/${jamNightId}/live`);
  };

  const handleChangeView = () => {
    setShowSelector(true);
  };

  const renderSelectedTable = () => {
    if (!selectedView || !jamNight) return null;

    switch (selectedView) {
      case "stage-combined":
        return (
          <div className="space-y-4">
            <UnifiedOnStageTable
              performers={performers}
              jamNightId={jamNightId}
              jamNight={jamNight}
              disabled={isEventCompleted}
              mode="jam-night"
            />
            <SelectedForStageTable
              performers={performers}
              jamNightId={jamNightId}
              disabled={isEventCompleted}
            />
          </div>
        );
      case "participants":
        return (
          <UnifiedParticipantsTable
            performers={performers}
            jamNightId={jamNightId}
            jamNight={jamNight}
            disabled={isEventCompleted}
            mode="jam-night"
          />
        );
      case "on-stage":
        return (
          <UnifiedOnStageTable
            performers={performers}
            jamNightId={jamNightId}
            jamNight={jamNight}
            disabled={isEventCompleted}
            mode="jam-night"
          />
        );
      case "selected":
        return (
          <SelectedForStageTable
            performers={performers}
            jamNightId={jamNightId}
            disabled={isEventCompleted}
          />
        );
      case "consideration":
        return (
          <ConsiderationTable
            performers={performers}
            jamNightId={jamNightId}
            disabled={isEventCompleted}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center min-h-screen">
        <div className="text-center relative z-10">
          <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading...</p>
        </div>
      </PageBackground>
    );
  }

  const currentView = VIEW_OPTIONS.find(opt => opt.id === selectedView);

  return (
    <PageBackground className="min-h-screen">
      {/* Always render the view if selected, or show loading state */}
      {selectedView ? (
        <>
          {/* Header */}
          <div className="sticky top-0 z-50 bg-white/30 backdrop-blur-xl border-b border-white/20"
            style={{
              boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}>
            <div className="container mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClose}
                    className="flex items-center gap-2 px-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  {currentView && (
                    <div className="flex items-center gap-2 ml-4">
                      {React.createElement(currentView.icon, { className: "h-5 w-5 text-slate-700 drop-shadow-sm" })}
                      <h1 className="text-lg font-semibold text-slate-800 drop-shadow-sm">{currentView.label}</h1>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleChangeView}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Change View</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="h-full overflow-y-auto pb-20 px-4 pt-4 relative z-10">
            {renderSelectedTable()}
          </div>
        </>
      ) : (
        // Show a loading/waiting state while dialog is open
        <div className="flex items-center justify-center h-screen relative z-10">
          <div className="text-center">
            <Eye className="h-8 w-8 mx-auto mb-4 text-slate-400" />
            <p className="text-slate-600">Select a view to continue</p>
          </div>
        </div>
      )}

      {/* Selection Dialog - overlays the content */}
      <Dialog open={showSelector} onOpenChange={(open) => {
        if (!open && !selectedView) {
          // If closing without selecting, go back to event page
          handleClose();
        } else {
          setShowSelector(open);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select View</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            {VIEW_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleViewSelect(option.id)}
                  className="flex items-start gap-3 p-4 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 text-left"
                >
                  <Icon className="h-5 w-5 mt-0.5 text-slate-700 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{option.label}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{option.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageBackground>
  );
}